import type { Tenant } from '@t4s/api-client/model'
import { useId, useState } from 'react'
import { DialogError, Kicker, ModalDialog, UnknownEnvironmentNotice } from './modal-dialog.tsx'

/**
 * Rotating a tenant's build key — docs/ux/PLATFORM-ADMIN.md §19, the kill switch that
 * replaced P03's "revoke a signing key" (`docs/DECISIONS.md`, "The build identifier is a
 * plain rotatable key on the tenant row, not a signed, kid-rotated token").
 *
 * The old signing-key model had two levers on two screens: rotating a key (not confirmed
 * — constructive, it changed nothing about what already worked) and revoking one (the
 * platform's single most destructive control, confirmed by typing the key id). Collapsing
 * both into one tenant-scoped rotation makes the surviving lever the destructive one:
 * **there is now only rotation, and rotating a tenant's build key immediately breaks
 * every build carrying the old value.** So this dialog is modelled on `RevokeDialog`'s
 * confirmation, not on P03's old rotate form, and it is held to the same construction as
 * `RetireDialog` — this application's other strongest confirmation: a real modal
 * `<dialog>` (`ModalDialog`: `showModal()` for native focus trapping, Escape refused, focus
 * returned to the opener), Cancel a real button, the danger action disabled until the
 * tenant's own slug is typed back.
 *
 * It states the two consequences plainly, because they are not symmetric
 * (`docs/DECISIONS.md`, "The build identifier is a plain rotatable key on the tenant row…",
 * "Rotation and the day-long TTL interact"): the web app recovers on its own, in seconds,
 * by re-fetching its config once it sees the rejection; a mobile build does not, because
 * its copy is baked in at build time and needs a new build and a new release to carry the
 * new key.
 *
 * **While the estate is unreadable it says so** (§5.1, added by P06): rotating the wrong
 * estate's key is the mistake the masthead's environment word exists to prevent, and a
 * console that cannot read it cannot be relied on to have said it.
 */

export type RotateBuildKeyDialogProps = {
  readonly tenant: Tenant
  readonly pending: boolean
  /** A failed confirm's sentence, shown inside the dialog so the typed slug is kept. */
  readonly error?: string | undefined
  readonly onCancel: () => void
  readonly onConfirm: () => void
}

export function RotateBuildKeyDialog({
  tenant,
  pending,
  error,
  onCancel,
  onConfirm,
}: RotateBuildKeyDialogProps) {
  const [typed, setTyped] = useState('')
  const slugId = useId()
  const headingId = useId()

  const canConfirm = typed === tenant.slug && !pending

  return (
    <ModalDialog
      labelledBy={headingId}
      foot={
        <div className="confirm-controls">
          <button type="button" className="button" onClick={onCancel} disabled={pending}>
            Cancel
          </button>
          <button
            type="button"
            className="button button-danger"
            disabled={!canConfirm}
            onClick={onConfirm}
          >
            {pending ? 'Rotating…' : 'Rotate this key'}
          </button>
        </div>
      }
    >
      <Kicker lead="No way back" />
      <h2 id={headingId}>Rotate {tenant.name}’s build key?</h2>

      <UnknownEnvironmentNotice />

      <ul>
        <li>
          <strong>
            Every build carrying the current key — every installed app, every cached web bundle —
            stops being able to sign in or read this tenant’s configuration, starting from its very
            next request.
          </strong>{' '}
          There is no grace period.
        </li>
        <li>
          <strong>The web app recovers on its own</strong>, usually within seconds: it notices the
          rejection and re-fetches its configuration once, bypassing the cache, and picks up the new
          key automatically.
        </li>
        <li>
          <strong>Mobile does not recover by itself.</strong> Its copy of the key is baked into the
          installed app. Whoever holds one needs a new build cut with the new key and released
          through the app store, and nothing here can make that happen faster.
        </li>
        <li>
          This does not sign anybody out. A session already issued keeps working until it expires on
          its own — this only stops new sign-ins and new unauthenticated requests.
        </li>
        <li>
          <strong>There is no way back to the old value.</strong> Rotating again only replaces this
          new key with yet another one.
        </li>
      </ul>

      <div className="typed">
        <label htmlFor={slugId}>
          Type <code>{tenant.slug}</code> to confirm
        </label>
        <input
          id={slugId}
          className="mono"
          value={typed}
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => {
            setTyped(event.target.value)
          }}
        />
        {typed === tenant.slug ? (
          <span className="typed-match" role="status">
            Matches — the button is enabled.
          </span>
        ) : null}
      </div>

      <DialogError>{error}</DialogError>
    </ModalDialog>
  )
}
